// frontend/src/components/approver/ApproverSidebar.tsx
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Squares2X2Icon,
  DocumentTextIcon,
  ClockIcon,
  CogIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { approverService, OvertimeRequest } from '../../services/approver.service';
import { AuthUser } from '../../services/auth.service';
import styles from './ApproverSidebar.module.css';

interface ApproverSidebarProps {
  activePage: string;
  onPageChange: (pageId: string) => void;
  isTransitioning?: boolean;
  onToggle?: (collapsed: boolean) => void;
  user?: AuthUser;
}

const ApproverSidebar: React.FC<ApproverSidebarProps> = ({
  activePage,
  onPageChange,
  isTransitioning = false,
  onToggle,
  user
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [recentRequests, setRecentRequests] = useState<OvertimeRequest[]>([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);

  const approverId = user?.employeeNo || user?.employeeNumber || user?.uid || '';

  // Load recent requests
  useEffect(() => {
    if (approverId) {
      loadRecentRequests();
    }
  }, [approverId]);

  const loadRecentRequests = async () => {
    try {
      setIsLoadingRecent(true);
      const recent = await approverService.getRecentRequests(approverId);
      setRecentRequests(recent.slice(0, 5)); // Show only 5 most recent
    } catch (error) {
      console.error('Error loading recent requests:', error);
    } finally {
      setIsLoadingRecent(false);
    }
  };

  const menuItems = useMemo(() => [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Squares2X2Icon className={styles.heroIcon} />,
      hasSubmenu: false
    },
    {
      id: 'all-requests',
      label: 'All Requests',
      icon: <DocumentTextIcon className={styles.heroIcon} />,
      hasSubmenu: false
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: <ChartBarIcon className={styles.heroIcon} />,
      hasSubmenu: false
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <CogIcon className={styles.heroIcon} />,
      hasSubmenu: false
    }
  ], []);

  const toggleSidebar = useCallback(() => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    
    if (onToggle) {
      onToggle(newCollapsedState);
    }
    
    console.log('Approver Sidebar toggled:', newCollapsedState ? 'collapsed' : 'expanded');
  }, [isCollapsed, onToggle]);

  const handleItemClick = useCallback((itemId: string) => {
    if (isTransitioning) {
      return;
    }

    if (activePage !== itemId) {
      onPageChange(itemId);
    }
    
    console.log(`Approver menu item clicked: ${itemId}`);
  }, [activePage, onPageChange, isTransitioning]);

  const isItemActive = useCallback((item: any) => {
    return activePage === item.id;
  }, [activePage]);

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#f59e0b';
      case 'approved':
        return '#10b981';
      case 'rejected':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  return (
    <aside className={`${styles.approverSidebar} ${isCollapsed ? styles.collapsed : ''} ${isTransitioning ? styles.transitioning : ''}`}>
      <div className={styles.sidebarHeader}>
        <button 
          className={styles.toggleButton}
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
          disabled={isTransitioning}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2"/>
            <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2"/>
            <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </button>
      </div>

      <nav className={styles.nav}>
        <ul className={styles.navList}>
          {menuItems.map((item) => {
            const isActive = isItemActive(item);
            
            return (
              <li key={item.id}>
                <button
                  className={`
                    ${styles.navItem} 
                    ${isActive ? styles.active : ''} 
                    ${isTransitioning ? styles.transitioning : ''}
                  `.trim()}
                  aria-label={item.label}
                  onClick={() => handleItemClick(item.id)}
                  disabled={isTransitioning}
                >
                  <span className={styles.navIcon}>
                    {item.icon}
                  </span>
                  <span className={styles.navLabel}>{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Recent Requests Section (Only when not collapsed) */}
      {!isCollapsed && (
        <div className={styles.recentSection}>
          <div className={styles.recentHeader}>
            <ClockIcon className={styles.recentIcon} />
            <h3 className={styles.recentTitle}>Recent Requests</h3>
          </div>

          {isLoadingRecent ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
            </div>
          ) : recentRequests.length > 0 ? (
            <div className={styles.recentList}>
              {recentRequests.map((request) => (
                <div
                  key={request.id}
                  className={styles.recentItem}
                  onClick={() => {
                    onPageChange('all-requests');
                  }}
                >
                  <div className={styles.recentItemHeader}>
                    <div 
                      className={styles.statusDot}
                      style={{ backgroundColor: getStatusColor(request.status) }}
                    ></div>
                    <span className={styles.projectName}>
                      {request.projectName}
                    </span>
                  </div>
                  <div className={styles.recentItemDetails}>
                    <span className={styles.requesterName}>{request.requesterName}</span>
                    <span className={styles.timeAgo}>{formatTimeAgo(request.requestTime)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.emptyRecent}>
              No recent requests
            </div>
          )}

          {recentRequests.length > 0 && (
            <button
              className={styles.viewAllButton}
              onClick={() => onPageChange('all-requests')}
            >
              View All Requests
            </button>
          )}
        </div>
      )}

      {/* User info footer (Only when not collapsed) */}
      {!isCollapsed && user && (
        <div className={styles.sidebarFooter}>
          <div className={styles.userInfo}>
            <div className={styles.userAvatar}>
              <div className={styles.avatarCircle}>
                {user.name ? user.name.charAt(0).toUpperCase() : 'A'}
              </div>
            </div>
            <div className={styles.userDetails}>
              <div className={styles.userName}>{user.name || 'Approver'}</div>
              <div className={styles.userRole}>Overtime Approver</div>
              <div className={styles.userDepartment}>{user.department || 'Management'}</div>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default ApproverSidebar;